import { useState } from 'react';

const faqs = [
  {
    question: 'Can men over 60 really build muscle and lose weight?',
    answer:
      'Yes — and the science backs it up. Men over 60 retain the ability to build lean muscle and burn fat with the right program. Mason Hale lost 55 lbs and built real strength at age 62 using a joint-safe, progressive system designed specifically for the over-60 body. The key is training smarter, not harder.',
  },
  {
    question: 'Is strength training safe for men over 60?',
    answer:
      'Strength training is not only safe for men over 60 — it is one of the most important things you can do for your long-term health. The Over-60 Strength Blueprint includes joint-safe movement alternatives for bad knees, shoulders, and back, so you can train hard without risking injury.',
  },
  {
    question: 'What is the best workout routine for a 60-year-old man to lose belly fat?',
    answer:
      'The most effective workout routine for men over 60 to lose belly fat combines progressive strength training with proper nutrition. Focus on compound movements like squats, deadlifts, and presses that build muscle and boost metabolism. The Over-60 Strength Blueprint includes a 12-week plan specifically designed to help men over 60 lose weight safely while protecting joints. Consistency with strength training 3-4 times per week, combined with a moderate calorie deficit, delivers the best results for losing belly fat after 60.',
  },
  {
    question: 'How many days a week should a man over 60 lift weights?',
    answer:
      'Most men over 60 see optimal results training 3-4 days per week with at least one rest day between sessions. This frequency allows enough stimulus for muscle growth while giving your body adequate recovery time. The Over-60 Strength Blueprint follows a 3-day-per-week structure with built-in recovery protocols. More is not always better — recovery becomes even more critical after 60, and overtraining can lead to injury and burnout.',
  },
  {
    question: 'How long before I see results from the Over-60 Strength Blueprint?',
    answer:
      'Most men notice measurable strength and body composition changes within 4–6 weeks. The full 12-week program is structured to deliver steady, lasting results — not a quick fix that fades.',
  },
  {
    question: 'What is included in the Over-60 Strength Blueprint?',
    answer:
      'You get a 12-week progressive workout plan, 40+ exercise video demonstrations, joint-safe movement alternatives, weekly progress tracking sheets, recovery and mobility protocols, and simple nutrition guidelines for muscle growth and fat loss. Everything is in one PDF — instant access after purchase.',
  },
  {
    question: 'Do I need a gym or special equipment?',
    answer:
      'The program is designed to be flexible. Many exercises can be done with minimal equipment at home. The blueprint includes both gym-based and home-friendly options so you can follow it wherever you train.',
  },
  {
    question: 'How much does the program cost?',
    answer:
      'The Over-60 Strength Blueprint is currently $19.99 — a one-time purchase with instant, lifetime access. This is introductory pricing and will increase soon. No subscription, no hidden fees.',
  },
];

export default function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section
      id="faq"
      className="py-20 sm:py-28 bg-[#111111] relative"
      aria-label="Frequently asked questions about over-60 fitness and strength training"
      itemScope
      itemType="https://schema.org/FAQPage"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c8a96e]/15 to-transparent" />

      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-px bg-[#c8a96e]" />
          <span className="text-[#c8a96e] text-xs font-black tracking-[0.3em] uppercase">FAQ</span>
        </div>

        <div className="mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-4">
            Common Questions
          </h2>
          <p className="text-gray-500 text-base leading-relaxed">
            Everything you need to know about getting fit over 60 and the Over-60 Strength Blueprint.
          </p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div
              key={i}
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
              className="border rounded-sm overflow-hidden transition-all duration-300"
              style={{
                borderColor: openIndex === i ? 'rgba(200,169,110,0.4)' : 'rgba(255,255,255,0.07)',
                background: openIndex === i ? 'rgba(200,169,110,0.04)' : 'rgba(255,255,255,0.02)',
              }}
            >
              <button
                onClick={() => toggle(i)}
                aria-expanded={openIndex === i}
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer group"
              >
                <h4
                  itemProp="name"
                  className="text-white text-sm sm:text-base font-bold leading-snug group-hover:text-[#c8a96e] transition-colors duration-200"
                >
                  {faq.question}
                </h4>
                <span
                  className="w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-sm border transition-all duration-300"
                  style={{
                    borderColor: openIndex === i ? 'rgba(200,169,110,0.5)' : 'rgba(255,255,255,0.1)',
                    background: openIndex === i ? 'rgba(200,169,110,0.1)' : 'transparent',
                    transform: openIndex === i ? 'rotate(45deg)' : 'rotate(0deg)',
                  }}
                >
                  <i className="ri-add-line text-[#c8a96e] text-sm" />
                </span>
              </button>

              <div
                style={{
                  maxHeight: openIndex === i ? '600px' : '0px',
                  overflow: 'hidden',
                  transition: 'max-height 0.4s cubic-bezier(0.16,1,0.3,1)',
                }}
              >
                <div
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                  className="px-6 pb-6"
                >
                  <p itemProp="text" className="text-gray-400 text-sm leading-[1.85] border-t border-white/5 pt-4">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Fitness Authority Backlinks */}
        <div className="mt-16 pt-10 border-t border-white/5">
          <p className="text-gray-600 text-xs font-black tracking-[0.25em] uppercase mb-6 text-center">
            Trusted Resources on Senior Fitness
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {[
              {
                label: 'NIH — Strength Training for Older Adults',
                href: 'https://www.nia.nih.gov/health/exercise-physical-activity/exercise-older-adults',
              },
              {
                label: 'Mayo Clinic — Fitness After 60',
                href: 'https://www.mayoclinic.org/healthy-lifestyle/fitness/in-depth/fitness/art-20044536',
              },
              {
                label: 'CDC — Physical Activity for Older Adults',
                href: 'https://www.cdc.gov/physicalactivity/basics/older_adults/index.htm',
              },
              {
                label: 'Harvard Health — Exercise & Aging',
                href: 'https://www.health.harvard.edu/topics/exercise-and-fitness',
              },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center gap-2 px-4 py-2.5 bg-white/3 border border-white/8 rounded-sm hover:border-[#c8a96e]/30 hover:bg-white/5 transition-all duration-200 cursor-pointer group"
              >
                <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-external-link-line text-[#c8a96e] text-xs" />
                </span>
                <span className="text-gray-500 text-xs group-hover:text-gray-300 transition-colors duration-200">
                  {link.label}
                </span>
              </a>
            ))}
          </div>
          <p className="text-gray-700 text-xs text-center mt-6 leading-relaxed max-w-xl mx-auto">
            The Over-60 Strength Blueprint is grounded in the same principles endorsed by leading health organizations —
            progressive resistance training, joint-safe movement, and sustainable nutrition for older adults.
          </p>
        </div>
      </div>
    </section>
  );
}
